import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Ban, ClipboardList, MessageSquare, PhoneForwarded, Plus, Volume2, Zap, } from 'lucide-react';
import { useGraphFrontendComponentAllowlist } from '../GraphFrontendComponentAllowlistContext';
import { cn } from '../lib/utils';
const NODE_CHOICES = [
    {
        kind: 'conversation',
        Icon: MessageSquare,
        label: 'Conversation node',
        hint: 'The agent talks with the caller, following a prompt.',
    },
    {
        kind: 'say',
        Icon: Volume2,
        label: 'Say node',
        hint: 'The agent delivers one line, then moves on.',
    },
    {
        kind: 'form',
        Icon: ClipboardList,
        label: 'Form node',
        hint: 'A block of structured questions the agent asks in order.',
    },
    {
        kind: 'end_conversation',
        Icon: Ban,
        label: 'End conversation node',
        hint: 'Hangs up the call when the graph reaches it.',
    },
    {
        kind: 'transfer',
        Icon: PhoneForwarded,
        label: 'Transfer node',
        hint: 'Dials a human, then routes on the outcome (success, declined, voicemail, timeout, failed).',
    },
    {
        kind: 'action',
        Icon: Zap,
        label: 'Action node',
        hint: 'The agent runs an action and routes on the outcome.',
    },
];
/** The node kinds this agent can add. Action nodes need an allowed action. */
export function useNodeChoices() {
    const allowlist = useGraphFrontendComponentAllowlist();
    return NODE_CHOICES.filter(choice => choice.kind !== 'action' || allowlist.allowedActionTypes.length > 0);
}
/** A "+" that opens the node kinds. The list renders on the page body, so
 *  canvas cards never cover it. */
export function InsertMenu({ kinds, visible, label, onChoose, }) {
    const choices = useNodeChoices().filter(choice => kinds.includes(choice.kind));
    const [at, setAt] = useState(null);
    const buttonRef = useRef(null);
    const menuRef = useRef(null);
    useEffect(() => {
        if (!at)
            return;
        const onPointerDown = (event) => {
            const target = event.target;
            if (target &&
                !buttonRef.current?.contains(target) &&
                !menuRef.current?.contains(target)) {
                setAt(null);
            }
        };
        const onKeyDown = (event) => {
            if (event.key === 'Escape')
                setAt(null);
        };
        window.addEventListener('mousedown', onPointerDown);
        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('mousedown', onPointerDown);
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [at]);
    if (choices.length === 0)
        return null;
    const open = at !== null;
    return (_jsxs(_Fragment, { children: [_jsx("button", { ref: buttonRef, type: "button", "aria-label": label, title: label, "aria-haspopup": "menu", "aria-expanded": open, 
                // The same gray as the lines, as an outline rather than a shadow.
                style: { borderColor: 'var(--xy-edge-stroke, #b1b1b7)' }, className: cn('nodrag nopan flex h-5 w-5 items-center justify-center rounded-full border bg-background text-foreground transition-opacity hover:bg-muted', visible || open ? 'opacity-100' : 'pointer-events-none opacity-0'), onClick: event => {
                    event.stopPropagation();
                    if (open) {
                        setAt(null);
                        return;
                    }
                    const rect = event.currentTarget.getBoundingClientRect();
                    setAt({ x: rect.right + 6, y: rect.top });
                }, children: _jsx(Plus, { className: "h-3 w-3" }) }), open &&
                createPortal(_jsx("div", { ref: menuRef, role: "menu", className: "fixed z-50 w-56 overflow-hidden rounded-md border border-border bg-popover shadow-md", style: { left: at.x, top: at.y }, children: choices.map(choice => (_jsxs("button", { type: "button", role: "menuitem", className: "flex w-full items-center gap-2.5 border-b px-3 py-2 text-left text-sm transition-colors last:border-b-0 hover:bg-muted/60", onClick: () => {
                            setAt(null);
                            onChoose(choice.kind);
                        }, children: [_jsx(choice.Icon, { className: "h-3.5 w-3.5 flex-shrink-0 text-primary" }), choice.label] }, choice.kind))) }), document.body)] }));
}
//# sourceMappingURL=InsertMenu.js.map